import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useState, useTransition } from "react";

import { config } from "shared/data";
import { accountAtom } from "shared/lib/account";
import { editionOptions, editions, useEdition, useEditionValue } from "shared/lib/edition";

import { bookmarkAtom } from "scmp-app/components/header/header-bookmark/atoms";

import IconArrow from "./icon-arrow.svg";
import LogoMynews from "./logo-mynews.svg";
import {
  ActionContainer,
  Container,
  Edition,
  EditionContainer,
  EditionMenu,
  EditionMenuItem,
  EditionText,
  StyledIconBookmark,
  StyledIconClose,
  StyledMynewsLink,
} from "./styles";

type Props = {
  className?: string;
  onCloseClick?: () => void;
};
export const HamburgerMenuHeader: FunctionComponent<Props> = ({ className, onCloseClick }) => {
  const currentEdition = useEditionValue();
  const { update: updateEdition } = useEdition();
  const { isLoggedIn } = useAtomValue(accountAtom);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorElement);
  const { showNewBookmarkAnimation: isShowBookmarkAlert, toggleShowNewBookmarkAnimation } =
    useAtomValue(bookmarkAtom);

  const bookmarkClickedHandler = () => {
    toggleShowNewBookmarkAnimation(false);
    const destination = `${config.account.scmpAccountHost}/manage/bookmark`;
    window.location.href = destination;
  };

  const editionClickedHandler = (edition: string) => {
    updateEdition(edition);
    window.location.href = editions[currentEdition].href;
  };

  // For preventing high INP for menu
  const [isLoading, startTransition] = useTransition();
  const [isFirstLoaded, setIsFirstLoaded] = useState(false);
  return (
    <Container className={className}>
      <EditionContainer>
        <EditionText>Edition:</EditionText>
        <Edition
          aria-controls={open ? "edition-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          id="edition-button"
          onClick={event => {
            startTransition(() => {
              setAnchorElement(event.currentTarget);
              setIsFirstLoaded(true);
            });
          }}
        >
          {isLoading && !isFirstLoaded ? "Loading..." : editions[currentEdition].label}
          <span>
            <IconArrow />
          </span>
        </Edition>
        <EditionMenu
          anchorEl={anchorElement}
          id="edition-menu"
          MenuListProps={{
            "aria-labelledby": "edition-button",
            sx: {
              padding: "8px 0px",
            },
          }}
          onClose={() => startTransition(() => setAnchorElement(null))}
          open={open}
          sx={{
            "&& .Mui-selected": {
              ":hover": {
                background: "transparent",
              },
              background: "transparent",
              color: "#000000",
            },
          }}
        >
          {editionOptions.map(option => (
            <EditionMenuItem
              key={option.value}
              onClick={() => editionClickedHandler(option.value)}
              selected={option.value === currentEdition}
            >
              {option.label}
            </EditionMenuItem>
          ))}
        </EditionMenu>
      </EditionContainer>
      <ActionContainer>
        {isLoggedIn && (
          <StyledIconBookmark $isNew={isShowBookmarkAlert} onClick={bookmarkClickedHandler} />
        )}
        <StyledMynewsLink pathname="/mynews">
          <LogoMynews />
        </StyledMynewsLink>
        <StyledIconClose onClick={onCloseClick} />
      </ActionContainer>
    </Container>
  );
};
